import React, {useState} from 'react'
import {LocalizationProvider, DatePicker} from '@mui/x-date-pickers'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material'
import {
  bowelPatternsOptions,
  bowelSoundsOptions,
  abdomenOptions,
  experiencingGIOptions,
  bladderOptions,
  yesNoOptions,
  yesNoOption,
  initialStateForm4 as initialState,
} from './NURSES_ASSESSMENT_FORM_CONSTANTS'

function CheckboxGroup({label, options, value, onChange}) {
  return (
    <FormControl component='fieldset' sx={{mb: 2}}>
      <Typography variant='subtitle2' color='primary'>
        {label}
      </Typography>
      <FormGroup row>
        {options.map((opt) => (
          <FormControlLabel
            key={opt.value}
            control={
              <Checkbox
                checked={value.includes(opt.value)}
                onChange={(e) => onChange(opt.value, e.target.checked)}
              />
            }
            label={opt.label}
            sx={{mr: 1, width: 300}}
          />
        ))}
      </FormGroup>
    </FormControl>
  )
}

const NursesAssessmentForm4 = ({data = {}, setFormValues, onSubmit, buttons}) => {
  const [formState, setFormState] = useState({...initialState, ...data})

  const handleChange = (name, value) => {
    setFormState((prev) => {
      const updated = {...prev, [name]: value}
      setFormValues && setFormValues(updated)
      return updated
    })
  }

  const handleCheckboxChange = (name, optionValue, checked) => {
    setFormState((prev) => {
      const arr = Array.isArray(prev[name]) ? [...prev[name]] : []
      if (checked) {
        if (!arr.includes(optionValue)) arr.push(optionValue)
      } else {
        const idx = arr.indexOf(optionValue)
        if (idx > -1) arr.splice(idx, 1)
      }
      const updated = {...prev, [name]: arr}
      setFormValues && setFormValues(updated)
      return updated
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit && onSubmit(formState)
  }

  return (
    <Box component='form' onSubmit={handleSubmit} sx={{mt: 2, mx: 'auto'}}>
      <Box>
        <Typography variant='h6' color='primary' sx={{fontSize: '16px', fontWeight: 'bold', mb: 2}}>
          Gastrointestinal & Genitourinary
        </Typography>
        {/* Gastrointestinal Section */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label='Last BM Date'
            value={formState.lastBMdate ? dayjs(formState.lastBMdate) : null}
            onChange={(newValue) => {
              handleChange('lastBMdate', newValue ? newValue.format('MM-DD-YYYY') : '')
            }}
            slotProps={{
              textField: {
                size: 'small',
                style: {width: '100%', marginBottom: '1rem'},
                InputProps: {
                  readOnly: true,
                  style: {cursor: 'pointer'},
                },
                onClick: (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  const iconButton =
                    e.currentTarget.querySelector('[data-testid="OpenPickerIconButton"]') ||
                    e.currentTarget.querySelector('.MuiIconButton-root')
                  if (iconButton) {
                    ;(iconButton as HTMLElement).click()
                  }
                },
              },
            }}
            sx={{
              flex: 1,
              '& .MuiInputBase-input': {
                WebkitTextFillColor: '#000000',
                cursor: 'pointer',
              },
              '& .MuiOutlinedInput-root': {
                cursor: 'pointer',
              },
              '& .MuiIconButton-root': {
                cursor: 'pointer',
              },
              '& .MuiPaper-root': {
                boxShadow:
                  '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
                borderRadius: '4px',
                overflow: 'hidden',
                marginTop: '4px',
              },
              '@media (max-width: 600px)': {
                '& .MuiPickersPopper-paper': {
                  position: 'fixed !important',
                  top: 'auto !important',
                  left: '50% !important',
                  transform: 'translateX(-50%) !important',
                  width: 'calc(100% - 32px) !important',
                  maxWidth: '360px !important',
                  margin: '0 auto !important',
                },
              },
            }}
            format='MM-DD-YYYY'
            disableFuture={true}
            disableOpenPicker={false}
          />
        </LocalizationProvider>
        <Box sx={{
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2, 
          mb: 2
        }}>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 33.33%' } }}>
            <InputLabel>Bowel Patterns</InputLabel>
            <Select
              value={formState.bowelPatterns}
              label='Bowel Patterns'
              onChange={(e) => handleChange('bowelPatterns', e.target.value)}
              size='small'
            >
              {bowelPatternsOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 33.33%' } }}>
            <InputLabel>Bowel Sounds</InputLabel>
            <Select
              value={formState.bowelSounds}
              label='Bowel Sounds'
              onChange={(e) => handleChange('bowelSounds', e.target.value)}
              size='small'
            >
              {bowelSoundsOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 33.33%' } }}>
            <InputLabel>Abdomen</InputLabel>
            <Select
              value={formState.abdomen}
              label='Abdomen'
              onChange={(e) => handleChange('abdomen', e.target.value)}
              size='small'
            >
              {abdomenOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <TextField
          label='Abdomen Location'
          value={formState.abdomenLocation}
          onChange={(e) => handleChange('abdomenLocation', e.target.value)}
          fullWidth
          sx={{mb: 2}}
        />
        {/* Genitourinary Section */}
        <Box sx={{
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2, 
          mb: 2
        }}>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 33.33%' } }}>
            <InputLabel>Experiencing</InputLabel>
            <Select
              value={formState.experiencingGI}
              label='Experiencing'
              onChange={(e) => handleChange('experiencingGI', e.target.value)}
              size='small'
            >
              {experiencingGIOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label='Urine Color'
            value={formState.urineColor}
            onChange={(e) => handleChange('urineColor', e.target.value)}
            fullWidth
            size='small'
            sx={{ flex: { xs: '1 1 100%', sm: '1 1 33.33%' } }}
          />
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 33.33%' } }}>
            <InputLabel>Bladder</InputLabel>
            <Select
              value={formState.bladder}
              label='Bladder'
              onChange={(e) => handleChange('bladder', e.target.value)}
              size='small'
            >
              {bladderOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{display: 'flex', gap: 2, mb: 2}}>
          <TextField
            label='Catheter Type'
            value={formState.catheterType}
            onChange={(e) => handleChange('catheterType', e.target.value)}
            fullWidth
          />
          <TextField
            label='Catheter Size'
            value={formState.catheterSize}
            onChange={(e) => handleChange('catheterSize', e.target.value)}
            fullWidth
          />
        </Box>
        <Box>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label='Date of Insertion'
              value={formState.catheterDate ? dayjs(formState.catheterDate) : null}
              onChange={(newValue) => {
                handleChange('catheterDate', newValue ? newValue.format('MM-DD-YYYY') : '')
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  style: {width: '100%', marginBottom:" 1rem"},
                  InputProps: {
                    readOnly: true,
                    style: {cursor: 'pointer'},
                  },
                  onClick: (e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    const iconButton =
                      e.currentTarget.querySelector('[data-testid="OpenPickerIconButton"]') ||
                      e.currentTarget.querySelector('.MuiIconButton-root')
                    if (iconButton) {
                      ;(iconButton as HTMLElement).click()
                    }
                  },
                },
              }}
              sx={{
                flex: 1,
                '& .MuiInputBase-input': {
                  WebkitTextFillColor: '#000000',
                  cursor: 'pointer',
                },
                '& .MuiOutlinedInput-root': {
                  cursor: 'pointer',
                },
                '& .MuiIconButton-root': {
                  cursor: 'pointer',
                },
                '& .MuiPaper-root': {
                  boxShadow:
                    '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginTop: '4px',
                },
                '@media (max-width: 600px)': {
                  '& .MuiPickersPopper-paper': {
                    position: 'fixed !important',
                    top: 'auto !important',
                    left: '50% !important',
                    transform: 'translateX(-50%) !important',
                    width: 'calc(100% - 32px) !important',
                    maxWidth: '360px !important',
                    margin: '0 auto !important',
                  },
                },
              }}
              format='MM-DD-YYYY'
              disableFuture={true}
              disableOpenPicker={false}
            />
          </LocalizationProvider>
          <FormControl fullWidth size='small'>
            <InputLabel>Change</InputLabel>
            <Select
              value={formState.catheterChange}
              label='Change'
              onChange={(e) => handleChange('catheterChange', e.target.value)}
              size='small'
              style={{marginBottom: '1rem'}}
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Typography variant='h6' color='primary' sx={{fontSize: '16px', fontWeight: 'bold', mb: 2}}>
         Musculoskeletal
        </Typography>
        {/* Musculoskeletal Section */}
        <CheckboxGroup
          label='Equal Strength'
          options={[
            {label: 'Equal LUE', value: 'equalLUE'},
            {label: 'Equal LLE', value: 'equalLLE'},
            {label: 'Equal RUE', value: 'equalRUE'},
            {label: 'Equal RLE', value: 'equalRLE'},
          ]}
          value={formState.equal}
          onChange={(val, checked) => handleCheckboxChange('equal', val, checked)}
        />
        <CheckboxGroup
          label='Strong Strength'
          options={[
            {label: 'Strong LUE', value: 'strongLUE'},
            {label: 'Strong LLE', value: 'strongLLE'},
            {label: 'Strong RUE', value: 'strongRUE'},
            {label: 'Strong RLE', value: 'strongRLE'},
          ]}
          value={formState.strong}
          onChange={(val, checked) => handleCheckboxChange('strong', val, checked)}
        />
        <CheckboxGroup
          label='Weak Strength'
          options={[
            {label: 'Weak LUE', value: 'weakLUE'},
            {label: 'Weak LLE', value: 'weakLLE'},
            {label: 'Weak RUE', value: 'weakRUE'},
            {label: 'Weak RLE', value: 'weakRLE'},
          ]}
          value={formState.weak}
          onChange={(val, checked) => handleCheckboxChange('weak', val, checked)}
        />
        <CheckboxGroup
          label='Paralysis'
          options={[
            {label: 'Paralysis LUE', value: 'paralysisLUE'},
            {label: 'Paralysis LLE', value: 'paralysisLLE'},
            {label: 'Paralysis RUE', value: 'paralysisRUE'},
            {label: 'Paralysis RLE', value: 'paralysisRLE'},
          ]}
          value={formState.paralysis}
          onChange={(val, checked) => handleCheckboxChange('paralysis', val, checked)}
        />
        <CheckboxGroup
          label='Spastic'
          options={[
            {label: 'Spastic LUE', value: 'spasticLUE'},
            {label: 'Spastic LLE', value: 'spasticLLE'},
            {label: 'Spastic RUE', value: 'spasticRUE'},
            {label: 'Spastic RLE', value: 'spasticRLE'},
          ]}
          value={formState.spastic}
          onChange={(val, checked) => handleCheckboxChange('spastic', val, checked)}
        />
        <CheckboxGroup
          label='Contracture'
          options={[
            {label: 'Contracture LUE', value: 'contractureLUE'},
            {label: 'Contracture LLE', value: 'contractureLLE'},
            {label: 'Contracture RUE', value: 'contactureRUE'},
            {label: 'Contracture RLE', value: 'contactureRLE'},
          ]}
          value={formState.contracture}
          onChange={(val, checked) => handleCheckboxChange('contracture', val, checked)}
        />
        <CheckboxGroup
          label='Flaccid'
          options={[
            {label: 'Flaccid LUE', value: 'flaccidLUE'},
            {label: 'Flaccid LLE', value: 'flaccidLLE'},
            {label: 'Flaccid RUE', value: 'flaccidRUE'},
            {label: 'Flaccid RLE', value: 'flaccidRLE'},
          ]}
          value={formState.flaccid}
          onChange={(val, checked) => handleCheckboxChange('flaccid', val, checked)}
        />
        <CheckboxGroup
          label='Amputation'
          options={[
            {label: 'Amputation LUE', value: 'amputationLUE'},
            {label: 'Amputation LLE', value: 'amputationLLE'},
            {label: 'Amputation RUE', value: 'amputationRUE'},
            {label: 'Amputation RLE', value: 'amputationRLE'},
          ]}
          value={formState.amputation}
          onChange={(val, checked) => handleCheckboxChange('amputation', val, checked)}
        />
        <Typography
          variant="h6"
          color="primary"
          sx={{ fontSize: '16px', fontWeight: 'bold', mb: 2 }}
        >
          Functional Status
        </Typography>

        <Typography
          variant="subtitle1"
          color="primary"
          sx={{ fontSize: '14px', fontWeight: 'bold', mb: 1 }}
        >
          Needs Assistance?
        </Typography>
        <Box sx={{
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2, 
          mb: 2
        }}>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Bed Mobility</InputLabel>
            <Select
              value={formState.bedMobility}
              label='Bed Mobility'
              onChange={(e) => handleChange('bedMobility', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Transfers</InputLabel>
            <Select
              value={formState.transfers}
              label='Transfers'
              onChange={(e) => handleChange('transfers', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Gait</InputLabel>
            <Select
              value={formState.gait}
              label='Gait'
              onChange={(e) => handleChange('gait', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Balance</InputLabel>
            <Select
              value={formState.balance}
              label='Balance'
              onChange={(e) => handleChange('balance', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {/* Communication/Cognitive Section */}
        <Box sx={{
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2, 
          mb: 2
        }}>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Comprehension</InputLabel>
            <Select
              value={formState.comprehension}
              label='Comprehension'
              onChange={(e) => handleChange('comprehension', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Expression</InputLabel>
            <Select
              value={formState.expression}
              label='Expression'
              onChange={(e) => handleChange('expression', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Cognition</InputLabel>
            <Select
              value={formState.cognition}
              label='Cognition'
              onChange={(e) => handleChange('cognition', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Reading/Writing</InputLabel>
            <Select
              value={formState.readingWriting}
              label='Reading/Writing'
              onChange={(e) => handleChange('readingWriting', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {/* ADL Section */}
        <Box sx={{
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2, 
          mb: 2
        }}>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Grooming</InputLabel>
            <Select
              value={formState.grooming}
              label='Grooming'
              onChange={(e) => handleChange('grooming', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Feeding</InputLabel>
            <Select
              value={formState.feeding}
              label='Feeding'
              onChange={(e) => handleChange('feeding', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Bathing</InputLabel>
            <Select
              value={formState.bathing}
              label='Bathing'
              onChange={(e) => handleChange('bathing', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Dressing</InputLabel>
            <Select
              value={formState.dressing}
              label='Dressing'
              onChange={(e) => handleChange('dressing', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <CheckboxGroup
          label='Assistive Devices'
          options={[
            {label: 'Wheelchair', value: 'Wheelchair'},
            {label: 'Cane', value: 'Cane'},
            {label: 'Cast', value: 'Cast'},
            {label: 'Walker', value: 'Walker'},
            {label: 'Braces', value: 'Braces'},
            {label: 'Other', value: 'Other'},
          ]}
          value={formState.assistiveDevices}
          onChange={(val, checked) => handleCheckboxChange('assistiveDevices', val, checked)}
        />
        <TextField
          label='Other Assistive Devices'
          value={formState.otherassistiveDevices}
          onChange={(e) => handleChange('otherassistiveDevices', e.target.value)}
          fullWidth
          sx={{mb: 2}}
        />
        {/* Skin Assessment Section */}
        <Typography variant='h6' color='primary' sx={{fontSize: '16px', fontWeight: 'bold', mb: 2}}>
         Skin Details
        </Typography>
        <CheckboxGroup
          label='Skin Color'
          options={[
            {label: 'Normal For Ethnicity', value: 'Normal'},
            {label: 'Pale', value: 'Pale'},
            {label: 'Flushed', value: 'Flushed'},
            {label: 'Jaundiced', value: 'Jaundiced'},
            {label: 'Mottled', value: 'Mottled'},
            {label: 'Cyanotic', value: 'Cyanotic'},
          ]}
          value={formState.skinColor}
          onChange={(val, checked) => handleCheckboxChange('skinColor', val, checked)}
        />
        <CheckboxGroup
          label='Skin Temperature'
          options={[
            {label: 'Hot', value: 'Hot'},
            {label: 'Cold', value: 'Cold'},
            {label: 'Dry', value: 'Dry'},
            {label: 'Diaphoretic', value: 'Diaphoretic'},
          ]}
          value={formState.skintempreture}
          onChange={(val, checked) => handleCheckboxChange('skintempreture', val, checked)}
        />
        <TextField
          label='Other Skin Temp Details'
          value={formState.otherskinTempDetails}
          onChange={(e) => handleChange('otherskinTempDetails', e.target.value)}
          fullWidth
          sx={{mb: 2}}
          multiline
          minRows={2}
        />
        <FormControl fullWidth size='small' sx={{mb: 2}}>
          <InputLabel>Turgor</InputLabel>
          <Select
            value={formState.turgor}
            label='Turgor'
            onChange={(e) => handleChange('turgor', e.target.value)}
            size='small'
          >
            {['Elastic', 'Decreased', 'Tenting', 'Other'].map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label='Other Turgor Details'
          value={formState.otherTurgorDetails}
          onChange={(e) => handleChange('otherTurgorDetails', e.target.value)}
          fullWidth
          sx={{mb: 2}}
          multiline
          minRows={2}
        />
        <FormControl fullWidth size='small' sx={{mb: 2}}>
          <InputLabel>Skin Texture</InputLabel>
          <Select
            value={formState.skinTexture}
            label='Skin Texture'
            onChange={(e) => handleChange('skinTexture', e.target.value)}
            size='small'
          >
            {['Smooth', 'Scaly', 'Other'].map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label='Other Skin Texture Details'
          value={formState.otherskinTextureDetails}
          onChange={(e) => handleChange('otherskinTextureDetails', e.target.value)}
          fullWidth
          sx={{mb: 2}}
          multiline
          minRows={2}
        />
        <Box sx={{
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2, 
          mb: 2
        }}>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Sensory</InputLabel>
            <Select
              value={formState.sensory}
              label='Sensory'
              onChange={(e) => handleChange('sensory', e.target.value)}
              size='small'
            >
              {['Completely limited', 'Very limited', 'Slightly limited', 'No impairment'].map(
                (opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Moisture</InputLabel>
            <Select
              value={formState.moisture}
              label='Moisture'
              onChange={(e) => handleChange('moisture', e.target.value)}
              size='small'
            >
              {['Constantly moist', 'Moist', 'Occasionally moist', 'Rarely moist'].map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Activity</InputLabel>
            <Select
              value={formState.activity}
              label='Activity'
              onChange={(e) => handleChange('activity', e.target.value)}
              size='small'
            >
              {['Bedfast', 'Chairfast', 'Walks frequently', 'Walks occasionally'].map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Mobility</InputLabel>
            <Select
              value={formState.mobility}
              label='Mobility'
              onChange={(e) => handleChange('mobility', e.target.value)}
              size='small'
            >
              {['Completely immobile', 'Very limited', 'Slightly limited', 'No limitations'].map(
                (opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2, 
          mb: 2
        }}>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Nutrition</InputLabel>
            <Select
              value={formState.nutrition}
              label='Nutrition'
              onChange={(e) => handleChange('nutrition', e.target.value)}
              size='small'
            >
              {['Very poor', 'Probably inadequate', 'Adequate', 'Excellent'].map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Friction</InputLabel>
            <Select
              value={formState.friction}
              label='Friction'
              onChange={(e) => handleChange('friction', e.target.value)}
              size='small'
            >
              {['Problem', 'Potential problem', 'No apparent problem'].map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Skin Precautions Implemented?</InputLabel>
            <Select
              value={formState.skinPrecatuions}
              label='Skin Precautions Implemented?'
              onChange={(e) => handleChange('skinPrecatuions', e.target.value)}
              size='small'
            >
              {yesNoOption.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 25%' } }}>
            <InputLabel>Wound Care team notified?</InputLabel>
            <Select
              value={formState.woundTeamNotified}
              label='Wound Care team notified?'
              onChange={(e) => handleChange('woundTeamNotified', e.target.value)}
              size='small'
            >
              {yesNoOption.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {/* Nutrition Section */}
        <Typography variant='h6' color='primary' sx={{fontSize: '16px', fontWeight: 'bold', mb: 2}}>
         Nutrition
        </Typography>
        <Box sx={{
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2, 
          mb: 2
        }}>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 33.33%' } }}>
            <InputLabel>Diet</InputLabel>
            <Select
              value={formState.diet}
              label='Diet'
              onChange={(e) => handleChange('diet', e.target.value)}
              size='small'
            >
              {['NPO', 'PO'].map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 33.33%' } }}>
            <InputLabel>Appetite</InputLabel>
            <Select
              value={formState.appetite}
              label='Appetite'
              onChange={(e) => handleChange('appetite', e.target.value)}
              size='small'
            >
              {['Good', 'Fair', 'Poor'].map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 33.33%' } }}>
            <InputLabel>Tube Feeding</InputLabel>
            <Select
              value={formState.tubeFeeding}
              label='Tube Feeding'
              onChange={(e) => handleChange('tubeFeeding', e.target.value)}
              size='small'
            >
              {['NG', 'G Tube', 'J Tube', 'PEG', 'Other'].map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <TextField
          label='Other Diet Details'
          value={formState.dietType}
          onChange={(e) => handleChange('dietType', e.target.value)}
          fullWidth
          sx={{mb: 2}}
          multiline
          minRows={2}
        />
        <TextField
          label='Supplements'
          value={formState.supplements}
          onChange={(e) => handleChange('supplements', e.target.value)}
          fullWidth
          sx={{mb: 2}}
          multiline
          minRows={2}
        />
        <TextField
          label='How Often?'
          value={formState.howOften}
          onChange={(e) => handleChange('howOften', e.target.value)}
          fullWidth
          sx={{mb: 2}}
          multiline
          minRows={2}
        />
        <TextField
          label='Restriction?'
          value={formState.restricitons}
          onChange={(e) => handleChange('restricitons', e.target.value)}
          fullWidth
          sx={{mb: 2}}
          multiline
          minRows={2}
        />
        <TextField
          label='Parental Feeding Rate'
          value={formState.parentalFeeding}
          onChange={(e) => handleChange('parentalFeeding', e.target.value)}
          fullWidth
          sx={{mb: 2}}
          multiline
          minRows={2}
        />
        <CheckboxGroup
          label='Experiencing (Nutrition)'
          options={[
            {label: 'Anorexia', value: 'Anorexia'},
            {label: 'Dehydration', value: 'Dehydration'},
          ]}
          value={formState.experiencing}
          onChange={(val, checked) => handleCheckboxChange('experiencing', val, checked)}
        />
        {/* Oral Section */}
        <Box sx={{
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2, 
          mb: 2
        }}>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 33.33%' } }}>
            <InputLabel>Mucosa Intact</InputLabel>
            <Select
              value={formState.mucosaIntact}
              label='Mucosa Intact'
              onChange={(e) => handleChange('mucosaIntact', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 33.33%' } }}>
            <InputLabel>Tongue Intact</InputLabel>
            <Select
              value={formState.tongueIntact}
              label='Tongue Intact'
              onChange={(e) => handleChange('tongueIntact', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size='small' sx={{ flex: { xs: '1 1 100%', sm: '1 1 33.33%' } }}>
            <InputLabel>Dentition Intact</InputLabel>
            <Select
              value={formState.dentitionIntact}
              label='Dentition Intact'
              onChange={(e) => handleChange('dentitionIntact', e.target.value)}
              size='small'
            >
              {yesNoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <TextField
          label='Other Details'
          value={formState.otherdetails}
          onChange={(e) => handleChange('otherdetails', e.target.value)}
          fullWidth
          sx={{mb: 2}}
          multiline
          minRows={2}
        />
        {buttons}
      </Box>
    </Box>
  )
}

export default NursesAssessmentForm4
